import * as React from 'react';
import { Redirect } from 'react-router-dom';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import bindAllOfThis from 'src/utils/BindThisHelper';
import Routes from 'src/core/Routes';
import "./Diagnostics.less";
import { PageProps } from "src/models/common/ComponentProps";
import { ReduxStoreModel } from "src/redux/ReduxModel";
import { Row, Col, Select, Icon, Input, Button, Collapse, Checkbox } from "antd";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import UserAction from "src/redux/UserActions";
import CheckpointAction from "src/redux/CheckpointActions";
import UserMeInfo from "src/models/account/UserMeInfo";
import { UserState } from "src/redux/UserReducer";
import { MissionState } from "src/redux/CheckpointReducer";
import { Grades } from "src/constants/Grades";
import { Planets } from "src/constants/Planets";
import { Checkpoints } from "src/constants/Checkpoints";
import { Roles } from "src/constants/Roles";
import DebugUtil from "src/utils/DebugUtil";
import { Games } from "src/constants/Games";
import SimpleCheckpointStatusDTO from "src/models/frontend/redux/SimpleCheckpointStatusDTO";
import FullCheckpointStatusDTO from "src/models/frontend/redux/FullCheckpointStatusDTO";

const { Option } = Select;

interface DiagnosticsProps extends PageProps<{}> {
    // From Redux
    ReduxState: ReduxStoreModel;
    setLogin: (request: UserMeInfo) => void;
    setCheckpoint: (request: FullCheckpointStatusDTO) => void;
}

interface DiagnosticsState {
    jsonBoxText: string;
    jsonBoxResultText: string;
    jsonKeepValues: boolean;
    intersectLeftText: string;
    intersectRightText: string;
    intersectResult: string;
    differenceLeft: string;
    differenceRight: string;
}

class Diagnostics extends React.Component<DiagnosticsProps, DiagnosticsState> {
    constructor(props: DiagnosticsProps) {
        super(props);
        bindAllOfThis(this, Diagnostics.prototype);

        this.state = {
            jsonBoxText: "",
            jsonBoxResultText: "",
            jsonKeepValues: true,
            intersectLeftText: "",
            intersectRightText: "",
            intersectResult: "",
            differenceLeft: "",
            differenceRight: ""
        };
    }

    handleJsonCheckboxChange(e: CheckboxChangeEvent) {
        this.setState({
            jsonKeepValues: e.target.checked
        });
    }
    handleJsonChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
        this.setState({
            jsonBoxText: e.target.value
        });
    }
    handleResultJsonChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
        this.setState({
            jsonBoxResultText: e.target.value
        });
    }
    handleIntersectLeftChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
        this.setState({
            intersectLeftText: e.target.value
        });
    }
    handleIntersectRightChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
        this.setState({
            intersectRightText: e.target.value
        });
    }

    squishJson(input: string, includeValues: boolean) {
        if (input == null || input.length < 1) {
            return;
        }

        let json = this.messyToClean(input);
        let output = this.squishJsonInternal(json, includeValues).join('\n');

        this.setState({
            jsonBoxResultText: output,
        });
    }
    squishJsonInternal(obj, includeValues: boolean, root: string = null): string[] {
        if (obj == null) {
            return [];
        }
        let keys = Reflect.ownKeys(obj);
        let output: string[] = [];

        for (const key of keys) {
            let subObj = obj[key];

            if (subObj != null && typeof subObj === 'object') {
                let result = this.squishJsonInternal(subObj, includeValues, root == null ? `${key.toString()}` : `${root}.${key.toString()}`);
                output = [...output, ...result];
            } else {
                output.push(`${root == null ? `${key.toString()}` : `${root}.${key.toString()}`} ${includeValues ? subObj : ""}`);
            }
        }
        return output;
    }
    unsquishJson(input: string, includeValues: boolean) {
        let output;

        // Try to split on newlines
        let jsons = input.split('\n');

        output = this.unsquishJsonInternal(jsons, includeValues);

        this.setState({
            jsonBoxText: JSON.stringify(output, null, 2)
        });
    }
    unsquishJsonInternal(jsons: string[], includeValues: boolean): object {
        // Inspiration taken from here
        // https://stackoverflow.com/questions/6393943/convert-javascript-string-in-dot-notation-into-an-object-reference
        let newObj = new Object();

        if (jsons == null || jsons.length < 1) {
            return [];
        }

        for (const json of jsons) {
            let object = newObj;
            let value = json.includes(' ') ? json.substring(json.indexOf(' ')).trim() : "";
            let splits = json.includes(' ') ? json.substring(0, json.indexOf(' ')).trim().split('.') : json.trim().split('.');
            for (var i = 0; i < splits.length - 1; i++) {
                let sup = object[splits[i]];
                if (sup == null) {
                    object = object[splits[i]] = {};
                } else {
                    object = object[splits[i]];
                }
            }
            object[splits[splits.length - 1]] = includeValues ? value : "";
        }

        return newObj;
    }
    messyToClean(json: string) {
        if (json == null || json.length < 3) {
            return null;
        }

        // Ensure property names are wrapped in quotes
        let cleanedJson = json.trim().split('\n').map(x => {
            let t = x.trim();

            if (!t.includes(':')) {
                return t;
            }

            if (t.slice(0, 1) !== "\"") {
                let first = t.substring(0, t.indexOf(':')).trim();
                let second = t.substring(t.indexOf(':') + 1).trim();
                return `"${first}": ${second}`;
            }
            return t;
        }).join('\n');

        return this.messyToCleanInternal(cleanedJson);
    }
    messyToCleanInternal(json: string, retry: number = 10) {
        if (retry < 1) {
            return null;
        }
        try {
            let result = JSON.parse(json);
            return result;
        } catch (error) {
            // Attempt some cleanup

            // Sometimes we are missing the braces all around the json
            if (json.slice(0, 1) !== '{') {
                let result = this.messyToCleanInternal("{" + json, retry - 1);
                if (result != null) {
                    return result;
                }
            }

            // Sometimes we have an extra ',' at the end
            if (json.slice(-1) === ',') {
                let result = this.messyToCleanInternal(json.slice(0, -1), retry - 1);
                if (result != null) {
                    return result;
                }
            }
            // Maybe we just need more braces
            return this.messyToCleanInternal(json + "}", retry - 1);
        }
    }

    intersectArrays(left: string, right: string) {
        if (left == null || right == null || left.length < 1 || right.length < 1) {
            return;
        }

        // Split, remove whitespace, remove empty
        let leftArray = left.split('\n').map(x => x.trim()).filter(x => x);
        let rightArray = right.split('\n').map(x => x.trim()).filter(x => x);

        let intersection = leftArray.filter(x => rightArray.includes(x));
        let differenceLeft = leftArray.filter(x => !rightArray.includes(x));
        let differenceRight = rightArray.filter(x => !leftArray.includes(x));

        // Remove duplicates
        intersection = intersection.filter((e, i, c) => c.indexOf(e) === i);
        differenceLeft = differenceLeft.filter((e, i, c) => c.indexOf(e) === i);
        differenceRight = differenceRight.filter((e, i, c) => c.indexOf(e) === i);

        this.setState({
            intersectResult: intersection.join('\n'),
            differenceLeft: differenceLeft.join('\n'),
            differenceRight: differenceRight.join('\n'),
        });
    }

    renderIds(user: UserState) {
        let state = "";
        let district = "";
        let school = "";

        if (user.stateId != null) {
            state = `State: ${user.stateId}`;
        }
        if (user.districtId != null) {
            district = `District: ${user.districtId}`;
        }
        if (user.schoolId != null) {
            school = `School: ${user.schoolId}`;
        }

        return `${state} ${district} ${school}`;
    }

    renderGameGuide() {
        return Games.All
            .sort((a, b) => b.grade.level - a.grade.level || a.planet.order - b.planet.order)
            .map(game => (<Row>
                <Col span={6}>{game.grade.name}</Col>
                <Col span={6}>{game.planet.name}</Col>
                <Col span={6}>{`${game.grade.shortName}${game.planet.name}`}</Col>
                <Col span={6}>{game.name}</Col>
            </Row>));
    }

    render() {
        if (!DebugUtil.isDebugEnabled()) {
            return <Redirect to={Routes.PAGE_NOT_FOUND} />;
        }
        let user = this.props.ReduxState.User;
        let mission = this.props.ReduxState.Mission;

        return <div className="diagnostics">
            <h2>Welcome to the Diagnostics Page</h2>
            <p>This page will show diagnostic information from your login, the project, translations and maybe some other stuff when I get around to it</p>

            {/* <Row></Row>
            <Col></Col> */}

            <Row gutter={16}>
                <Col xs={24} xl={12}>
                    <Collapse defaultActiveKey={['3']} onChange={() => { }}>
                        <Collapse.Panel header="Json Squisher" key="1">
                            <Row className="collapsed-grid">
                                <p>Changes json to and from dot notation</p>
                                <Col span={12}>
                                    <Row>Json Squish</Row>
                                    <Input.TextArea autosize={{ minRows: 8, maxRows: 24 }} value={this.state.jsonBoxText} onChange={(e) => this.handleJsonChange(e)} />
                                    <Button onClick={() => this.squishJson(this.state.jsonBoxText, this.state.jsonKeepValues)}>Squish</Button>
                                    <Checkbox checked={this.state.jsonKeepValues} onChange={e => this.handleJsonCheckboxChange(e)}>Retain Values</Checkbox>
                                </Col>
                                <Col span={12}>
                                    <Row>Json Un-Squish</Row>
                                    <Input.TextArea autosize={{ minRows: 8, maxRows: 24 }} value={this.state.jsonBoxResultText} onChange={(e) => this.handleResultJsonChange(e)} />
                                    <Button onClick={() => this.unsquishJson(this.state.jsonBoxResultText, this.state.jsonKeepValues)}>UnSquish</Button>
                                </Col>
                            </Row>
                        </Collapse.Panel>
                        <Collapse.Panel header="Array Intersect" key="2">
                            <Row className="collapsed-grid">
                                <p>Outputs values that are in both lists. Supports only lists, items separated by new line (\n)</p>
                                <Col span={12}>
                                    <Row>Left Source</Row>
                                    <Input.TextArea autosize={{ minRows: 8, maxRows: 16 }} value={this.state.intersectLeftText} onChange={(e) => this.handleIntersectLeftChange(e)} />
                                </Col>
                                <Col span={12}>
                                    <Row>Right Source</Row>
                                    <Input.TextArea autosize={{ minRows: 8, maxRows: 16 }} value={this.state.intersectRightText} onChange={(e) => this.handleIntersectRightChange(e)} />
                                    <Button onClick={() => this.intersectArrays(this.state.intersectLeftText, this.state.intersectRightText)}>Do the Check</Button>
                                </Col>
                            </Row>
                            <Row>
                                <Col span={8}>
                                    <Row>Left Only</Row>
                                    <Input.TextArea autosize={{ minRows: 8, maxRows: 24 }} value={this.state.differenceLeft} readOnly={true} />
                                </Col>
                                <Col span={8}>
                                    <Row>Both</Row>
                                    <Input.TextArea autosize={{ minRows: 8, maxRows: 24 }} value={this.state.intersectResult} readOnly={true} />
                                </Col>
                                <Col span={8}>
                                    <Row>Right Only</Row>
                                    <Input.TextArea autosize={{ minRows: 8, maxRows: 24 }} value={this.state.differenceRight} readOnly={true} />
                                </Col>
                            </Row>
                        </Collapse.Panel>
                    </Collapse>
                </Col>
                <Col xs={24} xl={12}>
                    <Collapse defaultActiveKey={['2']} onChange={() => { }}>
                        <Collapse.Panel header="Full User Info (Raw Output)" key="1">
                            <Row className="collapsed-grid">
                                <Col span={8}>
                                    <Row>Name</Row>
                                    <Row>Email</Row>
                                    <Row>IsEmulating?</Row>
                                    <Row>State/District/School Ids</Row>
                                    <Row>Cultures</Row>

                                    <Row>Role</Row>
                                    <Row>Grade</Row>
                                    <Row>Planet</Row>
                                    <Row>Checkpoint</Row>
                                </Col>
                                <Col span={16} className="gridlines">
                                    <Row>{`${user.lastName}, ${user.firstName}`}</Row>
                                    <Row>{user.email}</Row>
                                    <Row>{`${user.isEmulating}`}</Row>
                                    <Row>{this.renderIds(user)}</Row>
                                    <Row>{`CultureId: ${user.cultureId} CultureCode: ${user.cultureCode}`}</Row>

                                    <Row>{Roles.FindById(user.roleID).name}</Row>
                                    <Row>{Grades.FindById(user.gradeId).name}</Row>
                                    <Row>{Planets.FindById(mission.current.planetId).name}</Row>
                                    <Row>{Checkpoints.FindById(mission.current.checkpointId).name}</Row>
                                </Col>
                            </Row>
                        </Collapse.Panel>
                        <Collapse.Panel header="Game/Planet Guide" key="2">
                            <Col className="collapsed-grid">
                                <Row>
                                    <Col span={6}>Grade</Col>
                                    <Col span={6}>Planet</Col>
                                    <Col span={6}>Combo</Col>
                                    <Col span={6}>Game</Col>
                                </Row>
                                {this.renderGameGuide()}
                            </Col>
                        </Collapse.Panel>
                    </Collapse>
                </Col>
            </Row>
        </div>;
    }
}

function mapDispatchToProps(dispatch: Dispatch<any>) {
    return ({
        setLogin: (request: UserMeInfo) => UserAction.SetLogin(dispatch, request),
        setCheckpoint: (request: FullCheckpointStatusDTO) => CheckpointAction.Debug_SetCheckpoint(dispatch, request)
    });
}

function mapStateToProps(state: ReduxStoreModel) {
    return {
        // Note: You would not normally do this, do not copy this around the project
        ReduxState: state,
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(Diagnostics);



// WEBPACK FOOTER //
// ./src/pages/developer/Diagnostics.tsx